import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState, Platform } from 'react-native';
import {
  getChatwootUnread,
  markChatwootRead,
} from '../../api/chatwootSupport';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useAppSelector } from '../../redux/helper';
import ChatwootService, {
  type ChatwootCustomAttributes,
  type ChatwootState,
  type ChatwootUser,
} from '../../services/ChatwootService';
import AppSnackbar from '../../services/AppSnackbar';
import { useTheme } from '../../theme';

type ChatwootProviderProps = {
  children: React.ReactNode;
};

// --- Official Chatwoot web SDK types (sdk.js public API) ---

type ChatwootWidgetApi = {
  toggle: (state?: 'open' | 'close') => void;
  setUser: (identifier: string | number, user: Record<string, unknown>) => void;
  setCustomAttributes: (attributes: Record<string, unknown>) => void;
  toggleBubbleVisibility: (state: 'show' | 'hide') => void;
  reset: () => void;
};

type ChatwootWindow = Window & {
  chatwootSDK?: { run: (config: { websiteToken: string; baseUrl: string }) => void };
  $chatwoot?: ChatwootWidgetApi;
  chatwootSettings?: Record<string, unknown>;
};

const CHATWOOT_WEB_SCRIPT_ID = 'chatwoot-sdk-script';

// --- Shared helpers (identical to native ChatwootProvider.tsx) ---

const getString = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const getFirstString = (...values: unknown[]) => {
  for (const value of values) {
    const result = getString(value);
    if (result) return result;
  }
  return '';
};

type ChatwootActor = {
  ownerId: string;
  actorId: string;
  actorType: 'owner' | 'employee';
};

const buildChatwootActor = (
  employee: Record<string, any>,
): ChatwootActor | null => {
  // actor is not same as employee role.
  const ownerId = getFirstString(employee.bp_owner_id, employee.ownerId);
  const employeeId = getFirstString(employee._id, employee.bp_employee_id);
  const actorId = employeeId;
  if (!actorId) return null;
  return { ownerId, actorId, actorType: 'employee' };
};

const buildChatwootUser = (
  employee: Record<string, any>,
  actor: ChatwootActor | null,
): ChatwootUser | null => {
  const email = getFirstString(employee.bp_employee_email_address, employee.email);
  const phoneNumber = getFirstString(
    employee.bp_employee_phone_number,
    employee.bp_owner_phone_number,
    employee.phoneNumber,
  );
  const identifier = actor?.actorId || getFirstString(email, phoneNumber);
  if (!identifier) return null;

  return {
    identifier,
    name: getFirstString(employee.bp_employee_name, employee.name),
    email,
    phoneNumber,
    avatarUrl: getFirstString(
      employee.bp_employee_profile_picture_url,
      employee.avatar_url,
    ),
    identifierHash: getFirstString(
      employee.bp_chatwoot_identifier_hash,
      employee.chatwoot_identifier_hash,
      employee.identifier_hash,
    ),
  };
};

const buildCustomAttributes = (
  employee: Record<string, any>,
  business: Record<string, any>,
  actor: ChatwootActor | null,
): ChatwootCustomAttributes => ({
  app: 'BizPlus POS',
  platform: Platform.OS,
  bp_owner_id: actor?.ownerId,
  bp_actor_id: actor?.actorId,
  bp_actor_type: 'employee',
  business_name: getFirstString(business.name),
  owner_id: getFirstString(employee.bp_owner_id),
  employee_role: getFirstString(employee.bp_employee_role),
});

// --- Web SDK loader (module-level, one-shot) ---

const getWidget = (): ChatwootWidgetApi | null =>
  typeof window !== 'undefined'
    ? (window as ChatwootWindow).$chatwoot ?? null
    : null;

let sdkLoadPromise: Promise<void> | null = null;
let sdkReady = false;
let sdkInitKey = '';
let lastShownErrorId: string | null = null;

const loadWebSdk = (baseUrl: string): Promise<void> => {
  if (sdkLoadPromise) return sdkLoadPromise;

  const cw = window as ChatwootWindow;
  if (cw.chatwootSDK) return Promise.resolve();

  sdkLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(
      CHATWOOT_WEB_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (existing) {
      if ((window as ChatwootWindow).chatwootSDK) { resolve(); return; }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Chatwoot web SDK.')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = CHATWOOT_WEB_SCRIPT_ID;
    script.src = `${baseUrl}/packs/js/sdk.js`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load Chatwoot web SDK.'));
    document.body.appendChild(script);
  });

  return sdkLoadPromise;
};

// --- Provider ---

const ChatwootProvider: React.FC<ChatwootProviderProps> = ({ children }) => {
  const { isDark } = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const employee = useAppSelector(s => s.auth.inApp.account.employeeInfo);
  const business = useAppSelector(s => s.auth.inApp.account.businessInfo);
  const [snapshot, setSnapshot] = useState<ChatwootState>(
    ChatwootService.getSnapshot(),
  );

  useEffect(() => {
    ChatwootService.initialize();
    return ChatwootService.subscribe(setSnapshot);
  }, []);

  const chatwootActor = useMemo(
    () => buildChatwootActor(employee || {}),
    [employee],
  );
  const chatwootUser = useMemo(
    () => buildChatwootUser(employee || {}, chatwootActor),
    [chatwootActor, employee],
  );
  const customAttributes = useMemo(
    () => buildCustomAttributes(employee || {}, business || {}, chatwootActor),
    [business, chatwootActor, employee],
  );

  // --- Shared business logic (mirrors native exactly) ---

  const refreshUnread = useCallback(async () => {
    if (!auth?.accessToken || !chatwootActor) {
      ChatwootService.clearUnread();
      return;
    }
    try {
      const unreadState = await getChatwootUnread(axiosPrivate);
      ChatwootService.setUnreadFromApi(unreadState);
    } catch {
      // Background badge sync should not interrupt the user's current flow.
    }
  }, [auth?.accessToken, axiosPrivate, chatwootActor]);

  const openPendingPushChat = useCallback(async () => {
    const shouldOpen = await ChatwootService.consumePendingPushOpen();
    if (!shouldOpen) return;
    await ChatwootService.openChat({ requestNotificationPermission: false });
  }, []);

  useEffect(() => {
    if (chatwootUser) {
      ChatwootService.identifyUser(chatwootUser, customAttributes);
    } else {
      ChatwootService.identifyAnonymous(customAttributes);
    }
  }, [chatwootUser, customAttributes]);

  useEffect(() => {
    void refreshUnread();
  }, [refreshUnread]);

  useEffect(() => {
    if (!snapshot.initialized) return;
    void openPendingPushChat();
  }, [openPendingPushChat, snapshot.initialized]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active') {
        void refreshUnread();
        void openPendingPushChat();
      }
    });
    return () => subscription.remove();
  }, [openPendingPushChat, refreshUnread]);

  useEffect(
    () =>
      ChatwootService.subscribeToOpen(async () => {
        if (!auth?.accessToken || !chatwootActor) return;
        try {
          const unreadState = await markChatwootRead(axiosPrivate);
          ChatwootService.setUnreadFromApi(unreadState);
        } catch {
          await refreshUnread();
        }
      }),
    [auth?.accessToken, axiosPrivate, chatwootActor, refreshUnread],
  );

  useEffect(() => {
    const error = snapshot.lastError;
    if (!error || error.id === lastShownErrorId) return;
    lastShownErrorId = error.id;
    AppSnackbar.show(error.message, 'error');
  }, [snapshot.lastError]);

  // --- Web SDK: load once, hide bubble, no global launcher ---

  useEffect(() => {
    const { baseUrl, websiteToken, locale, colorScheme } = snapshot.config;
    if (!baseUrl || !websiteToken) return;

    const configKey = `${baseUrl}:${websiteToken}`;
    let cancelled = false;

    void (async () => {
      try {
        const cw = window as ChatwootWindow;
        cw.chatwootSettings = {
          ...(cw.chatwootSettings || {}),
          hideMessageBubble: true,
          showUnreadMessagesDialog: false,
          locale,
          darkMode: isDark ? 'dark' : colorScheme,
        };

        await loadWebSdk(baseUrl);
        if (cancelled) return;

        if (sdkInitKey !== configKey) {
          cw.chatwootSDK?.run({ websiteToken, baseUrl });
          sdkInitKey = configKey;
        }

        if (cw.$chatwoot) {
          sdkReady = true;
          cw.$chatwoot.toggleBubbleVisibility('hide');
        }
      } catch {
        // Non-critical: user sees error only when they actually click support.
      }
    })();

    return () => { cancelled = true; };
  }, [
    isDark,
    snapshot.config.baseUrl,
    snapshot.config.colorScheme,
    snapshot.config.locale,
    snapshot.config.websiteToken,
  ]);

  // SDK ready: sync identity once, enforce hidden bubble
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleReady = () => {
      sdkReady = true;
      const widget = getWidget();
      if (!widget) return;

      widget.toggleBubbleVisibility('hide');

      const current = ChatwootService.getSnapshot();
      if (current.user?.identifier) {
        widget.setUser(current.user.identifier, {
          name: current.user.name,
          email: current.user.email,
          phone_number: current.user.phone_number,
          avatar_url: current.user.avatar_url,
          identifier_hash: current.user.identifier_hash,
        });
      }
      if (Object.keys(current.customAttributes).length > 0) {
        widget.setCustomAttributes(current.customAttributes);
      }
      if (current.isVisible) {
        widget.toggle('open');
      }
    };

    const handleClosed = () => {
      if (ChatwootService.getSnapshot().isVisible) {
        ChatwootService.closeChat();
      }
    };

    window.addEventListener('chatwoot:ready', handleReady as EventListener);
    window.addEventListener('chatwoot:closed', handleClosed as EventListener);
    return () => {
      window.removeEventListener('chatwoot:ready', handleReady as EventListener);
      window.removeEventListener('chatwoot:closed', handleClosed as EventListener);
    };
  }, []);

  // Sync user/attributes on change (after SDK ready)
  useEffect(() => {
    if (!sdkReady) return;
    const widget = getWidget();
    if (!widget) return;

    if (snapshot.user?.identifier) {
      widget.setUser(snapshot.user.identifier, {
        name: snapshot.user.name,
        email: snapshot.user.email,
        phone_number: snapshot.user.phone_number,
        avatar_url: snapshot.user.avatar_url,
        identifier_hash: snapshot.user.identifier_hash,
      });
    } else {
      widget.reset();
    }

    if (Object.keys(snapshot.customAttributes).length > 0) {
      widget.setCustomAttributes(snapshot.customAttributes);
    }
  }, [snapshot.user, snapshot.customAttributes]);

  // Open/close chat panel (app-triggered only, no bubble)
  useEffect(() => {
    if (!sdkReady) return;
    const widget = getWidget();
    if (!widget) return;
    widget.toggle(snapshot.isVisible ? 'open' : 'close');
  }, [snapshot.isVisible]);

  return <>{children}</>;
};

export default ChatwootProvider;
